/* 地址公共接口调用方法 */

// 三方库/框架
import { getServerTranslation } from '@/i18n';
import to from 'await-to-js';
import i18next from 'i18next';
// 公共方法
import {
  initAddress,
  isAddress,
  isPostCodeAddress,
  setAddressCookie,
  getAddressType,
} from '@/utils/addressUtils';
import { queryGlobalCountriesData } from '@/utils/countryUtils';
import { isNextServer } from '@/intl-jdi-common-ssr/utils/uaParser';
// 接口
import {
  getUserAddressList,
  getRecommendPickUpList,
  queryPostCode,
} from '@/apis/address/address.api';
import { Address } from '@/apis/address/address.interface';
import { getLoginInfoApi } from '@/apis/login/login.api';

import { ADDRESS_CACHE_TAG_KEY } from '@/constants/address';

export const curPostCodeOrAddress = async (isDetail = false) => {
  const data = await initAddress();
  const translation = isNextServer() ? await getServerTranslation() : i18next;
  // 获取缓存postCode
  if (isPostCodeAddress(data)) {
    // 如果缓存的兜底地址 需要展示默认UK
    if (data?.isDefaultLocal) {
      return ''; //`${translation.t("common.deliverToUk")}`;
    }
    return `${translation.t('common.deliverTo')}` + ' ' + data?.postCode;
  }
  if (isAddress(data)) {
    // 商详展示postcode
    if (isDetail) {
      return `${translation.t('common.deliverTo')}` + ' ' + data?.countyName + ' ' + data?.postCode;
    }
    return `${translation.t('common.deliverTo')}` + ' ' + data?.postCode;
  }
  const [_, loginRes] = await getLoginInfoApi({ types: [6] }, { meta: { forceIgnoreLogin: true } });
  if (!(loginRes && loginRes.success && loginRes.data)) {
    return ''; //`${translation.t("common.deliverToUk")}`;
  }
  // 如果没有缓存，匹配收货地址（默认收货地址、第一天收货地址），（后端排序规则：有默认则默认在第一条）
  const [err, result] = await to(getUserAddressList());
  const list = result?.data?.addressList;
  if (list && list.length > 0) {
    if (isDetail) {
      return (
        `${translation.t('common.deliverTo')}` + ' ' + list[0]?.countyName + ' ' + list[0]?.postCode
      );
    }
    return `${translation.t('common.deliverTo')}` + ' ' + list[0]?.postCode;
  }
  return ''; //`${translation.t("common.deliverToUk")}`;
};

// 更新缓存地址数据 适用于刷数场景
export const updateCacheAddress = async (cacheAddress: Address): Promise<boolean> => {
  try {
    const type = getAddressType(cacheAddress);

    // 没有缓存地址或类型无法识别 返回失败
    if (!cacheAddress || !type) return false;

    switch (type) {
      case 'delivery': {
        const [, result] = await to(getUserAddressList({ deliverPile: true }));

        const list = result?.data?.addressList || [];
        const matchData = list?.find((item) => item?.addressId === cacheAddress?.addressId);
        if (matchData) {
          setAddressCookie(matchData);
          return true;
        }
        return false;
      }

      case 'pickup': {
        const params = {
          countryCode: cacheAddress?.countryCode,
          latitude: cacheAddress?.latitude,
          longitude: cacheAddress?.longitude,
          postCode: cacheAddress?.postCode,
        };

        const [, result] = await to(getRecommendPickUpList(params));

        const list = result?.data || [];
        const matchData = list?.find(
          (item: Address) => item?.pickUpSiteId === cacheAddress?.pickUpSiteId,
        );
        if (matchData) {
          setAddressCookie(matchData);
          return true;
        }
        return false;
      }

      case 'postcode': {
        const postCodeValue = cacheAddress?.postCode?.replace(/\s+/g, '') || '';
        const [, result] = await to(
          queryPostCode({
            postCode: postCodeValue ? postCodeValue.toUpperCase() : postCodeValue,
            checkDeliverableFlag: true,
            countryId: cacheAddress?.countryId,
            countryCode: cacheAddress?.countryCode,
          }),
        );

        if (result?.data) {
          setAddressCookie(result.data);
          return true;
        }
        return false;
      }

      default:
        return false;
    }
  } catch (error) {
    console.error('Failed to update cache address:', error);
    return false;
  }
};

// 没有缓存的时候，获取默认或者第一条作为缓存数据，目前登录、当前缓存被删除的时候需要存储下
export const setDefaultLocalAddress = async (params?: {
  currentCountry?: any;
  currentCountryCode?: string;
  updateAddressCacheTag?: string;
}) => {
  const { currentCountry, updateAddressCacheTag } =
    params?.currentCountry && params?.currentCountryCode
      ? { ...params }
      : await queryGlobalCountriesData();

  // 如果有缓存就直接返回
  const data = await initAddress();
  if (data && data?.postCode && data?.provinceId) {
    try {
      const localAddressCacheTag = window.localStorage.getItem(ADDRESS_CACHE_TAG_KEY);
      if (updateAddressCacheTag && updateAddressCacheTag !== localAddressCacheTag) {
        // 后端下发更新缓存标识 不一致则更新
        const updateRes = await updateCacheAddress(data);
        if (updateRes) {
          // 更新成功 写入localStorage
          window.localStorage.setItem(ADDRESS_CACHE_TAG_KEY, updateAddressCacheTag);
        }
      }
    } catch (error) {
      console.error('Failed to update cache address:', error);
    }
    return;
  }

  // 存缓存，匹配收货地址（默认收货地址、第一天收货地址），（后端排序规则：有默认则默认在第一条）
  const [err, result] = await to(
    getUserAddressList({
      deliverPile: true, // 是否分堆
    }),
  );
  const list = result?.data?.addressList;
  if (list && list.length > 0) {
    setAddressCookie(list[0]);
    return;
  }
  // 如果没有收货地址则缓存兜底地址
  const {
    areaCode,
    countryCode,
    countryId,
    name,
    siteCode,
    siteId,
    postCode,
    longitude,
    latitude,
    provinceId = 0,
    cityId = 0,
    districtId = 0,
    townId = 0,
  } = currentCountry || {};
  const defaultAddress = {
    areaCode,
    countryCode,
    countryId,
    countryName: name,
    siteCode,
    siteId,
    postCode,
    longitude,
    latitude,
    provinceId,
    cityId,
    districtId,
    townId,
  } as Address;
  setAddressCookie(defaultAddress);
};

/**
 * 获取用户配送地址
 * @returns
 */
export const curShippingAddress = async () => {
  const data = await initAddress();
  // 获取缓存postCode
  if (data?.postCode && data?.addressId) {
    return data;
  }
  // 如果没有缓存，匹配收货地址（默认收货地址、第一天收货地址），（后端排序规则：有默认则默认在第一条）
  const [err, result] = await to(getUserAddressList());
  const list = result?.data?.addressList;
  if (list && list.length > 0) {
    if (isPostCodeAddress(data)) {
      const matchedIndex = list.findIndex((item) => item.postCode === data?.postCode);
      if (matchedIndex !== -1) {
        return list[matchedIndex];
      }
    }
    return list[0];
  }
  return null;
};
